import PropTypes from 'prop-types';
import { BsBuilding, BsChevronRight } from 'react-icons/bs';

import useContactRoles from '#globalHooks/useContactRoles';
import { getInitials } from '#lib/textUtils';

import { groupLookupParties } from './callOverlayUtils';

const AVATAR_CLS =
	'rounded-full bg-primary-600 text-white flex items-center justify-center font-semibold flex-shrink-0';

/**
 * Heading label for a party: the company name, else the (single) contact's name,
 * else a neutral fallback.
 */
function partyTitle(party) {
	if (party.companyName) return party.companyName;
	return party.contacts[0]?.name || 'Unbekannter Kontakt';
}

const PartyRow = ({ party, onSelect }) => {
	const { roleLabel } = useContactRoles();
	const title = partyTitle(party);
	const clickable = Boolean(party.companyId);
	// Contacts shown as a sub-line; when the party has no company, the first contact IS the title.
	const subContacts = party.companyName ? party.contacts : party.contacts.slice(1);

	return (
		<button
			type="button"
			onClick={clickable ? () => onSelect(party.companyId) : undefined}
			disabled={!clickable}
			className={`w-full flex items-center gap-3 px-3 py-2 rounded-card text-left transition-colors ${
				clickable ? 'bg-bgBase-300 hover:bg-primary-100 cursor-pointer group' : 'bg-bgBase-300 cursor-default'
			}`}
		>
			<div className={`w-9 h-9 text-xs ${AVATAR_CLS}`}>
				{party.companyName ? <BsBuilding className="w-4 h-4" /> : getInitials(title)}
			</div>
			<div className="min-w-0 flex-1">
				<p className="text-sm font-semibold text-text truncate">{title}</p>
				{subContacts.length > 0 && (
					<p className="text-[11px] text-bgBase-800 truncate">
						{subContacts
							.map((c) => (c.role ? `${c.name} · ${roleLabel(c.role)}` : c.name))
							.filter(Boolean)
							.join(', ')}
					</p>
				)}
			</div>
			{clickable && (
				<BsChevronRight className="w-3 h-3 text-bgBase-800 group-hover:text-primary-500 flex-shrink-0" />
			)}
		</button>
	);
};

PartyRow.propTypes = {
	party: PropTypes.shape({
		companyId: PropTypes.string,
		companyName: PropTypes.string,
		contacts: PropTypes.arrayOf(
			PropTypes.shape({ id: PropTypes.string, name: PropTypes.string, role: PropTypes.string }),
		).isRequired,
	}).isRequired,
	onSelect: PropTypes.func.isRequired,
};

/**
 * Caller identification for the call overlay: resolves an incoming number to
 * CRM contacts/companies and lists every match, grouped by company. Renders
 * nothing while loading or when there is no match (the overlay then just shows
 * the raw number). Clicking a company navigates to its detail page.
 */
const CallPartyInfo = ({ lookup, onSelectCompany }) => {
	const parties = groupLookupParties(lookup);
	if (parties.length === 0) return null;

	return (
		<div className="space-y-1.5 max-h-40 overflow-y-auto">
			{parties.map((party, idx) => (
				<PartyRow
					key={party.companyId ?? `orphan-${party.contacts[0]?.id ?? idx}`}
					party={party}
					onSelect={onSelectCompany}
				/>
			))}
		</div>
	);
};

CallPartyInfo.propTypes = {
	lookup: PropTypes.shape({
		contacts: PropTypes.array,
		companies: PropTypes.array,
	}),
	onSelectCompany: PropTypes.func.isRequired,
};

export default CallPartyInfo;
